import clsx from 'clsx';
import { Badge } from '../../../components/ui/Badge';
import { Nil } from '../../../components/ui/Table';
import { formatDayMonthYear } from '../../../utils/dateFormat';
import { formatShortDateTime } from '../shared';
import type { CampaignRef, FunnelCounts, SyncFailure } from '../../../api/outreach';

/** lemlist pulls activity hourly and runs the full sync daily at ~05:00 UTC; older than this means syncs were missed. */
export const STALE_SYNC_HOURS = 26;

export const EMPTY_PERIOD_LABEL = 'No activity yet in this period.';

/** 0.125 → "12.5%", 0.5 → "50%", null → "—". */
export function formatRate(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return '—';
  const rounded = Math.round(value * 1000) / 10;
  return `${Number.isInteger(rounded) ? rounded.toFixed(0) : rounded.toFixed(1)}%`;
}

export function ratio(numerator: number, denominator: number): number | null {
  return denominator > 0 ? numerator / denominator : null;
}

/** A rate with its muted "n/N" as a sub-line under the headline percentage. */
/**
 * `unit` names the denominator ("38 of 136 invited"), so a rate whose base is
 * not the Contacted column beside it (people invited vs people contacted by any
 * step) can't read as a mismatch.
 */
export function Rate({ value, n, total, unit }: { value: number | null; n: number; total: number; unit?: string }) {
  if (value === null || !Number.isFinite(value)) return <Nil />;
  return (
    <span className="tabular-nums whitespace-nowrap">
      <span className="block">{formatRate(value)}</span>
      {total > 0 && (
        <span className="block text-body-xs text-text-tertiary">
          {unit ? `${n.toLocaleString('en-GB')} of ${total.toLocaleString('en-GB')} ${unit}` : `${n}/${total}`}
        </span>
      )}
    </span>
  );
}

/** "7 Oct, 14:30"; "7 Oct 2025, 14:30" outside the current year. */
export function formatDateTime(iso: string): string {
  return formatShortDateTime(iso);
}

/** "7 Oct 2026". */
export function formatDate(iso: string): string {
  return formatDayMonthYear(iso);
}

export function hoursSince(iso: string): number {
  return (Date.now() - new Date(iso).getTime()) / 3_600_000;
}

/** "The daily lemlist sync failed 3 h ago (29 Sep, 05:02): 401 Unauthorized." */
export function syncFailureText(failure: SyncFailure): string {
  const which = failure.run === 'daily' ? 'daily lemlist sync' : 'hourly lemlist activity sync';
  return `The latest ${which} failed ${formatRelative(failure.at)} (${formatDateTime(failure.at)})${failure.error ? `: ${failure.error}` : ''}.`;
}

export function formatRelative(iso: string): string {
  const minutes = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${hours} h ago`;
  return `${Math.floor(hours / 24)} days ago`;
}

/** "[SIGNAL 04A]" (the prefix the API returns per play) → "S04A". */
export function signalCode(campaignPrefix: string): string | null {
  const match = /^\[SIGNAL\s+(\d+[A-Z]?)\]/i.exec(campaignPrefix.trim());
  return match ? `S${match[1]!.toUpperCase()}` : null;
}

export function signalTabLabel(campaignPrefix: string, label: string): string {
  const code = signalCode(campaignPrefix);
  if (!code || label.startsWith(code) || label.startsWith('[SIGNAL')) return label;
  return `${code} ${label}`;
}

/**
 * Pure: what a signal's lemlist campaign name adds to the signal column, or
 * null when it only repeats it. "[SIGNAL 02] Won a public tender" next to
 * "S02 Won a public tender" says nothing (Outbound review #9, 12 Oct);
 * "[SIGNAL 04A] Contract up for re-tender" next to "Re-tender · defend" does,
 * so it shows without the "[SIGNAL 04A]" code the signal column carries.
 * `displayName` is the name as the table would draw it (PAUSED / LinkedIn
 * suffix already dropped where the page says it once).
 */
export function campaignBeyondSignal(displayName: string, campaignPrefix: string, label: string): string | null {
  const rest = displayName.replace(/^\s*\[SIGNAL\s+\d+[A-Z]?\]\s*/i, '').trim();
  const norm = (v: string) => v.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  if (!rest) return null;
  return norm(rest) === norm(label) || norm(rest) === norm(signalTabLabel(campaignPrefix, label)) ? null : rest;
}

export function lemlistCampaignUrl(campaignId: string): string {
  return `https://app.lemlist.com/campaigns/${campaignId}`;
}

/** A count cell: right-aligned tabular digits, zero shown as a quiet 0 rather than a dash. */
export function Num({ value, strong = false }: { value: number | null; strong?: boolean }) {
  if (value === null) return <Nil />;
  return (
    <span className={clsx('tabular-nums', value === 0 ? 'text-text-tertiary' : strong && 'font-medium')}>
      {value.toLocaleString('en-GB')}
    </span>
  );
}

/**
 * The campaign name without a trailing "· PAUSED" that the team types into
 * lemlist names, when lemlist itself says the campaign is paused: the status is
 * already stated once on the page, so the name need not repeat it.
 */
export function campaignDisplayName(campaign: CampaignRef): string {
  if (campaign.status?.toLowerCase() !== 'paused') return campaign.name;
  return campaign.name.replace(/\s*[·|-]\s*paused\s*$/i, '').trim() || campaign.name;
}

/** The " · LinkedIn" channel suffix the team types at the end of lemlist campaign names. */
const LINKEDIN_SUFFIX = /\s*·\s*LinkedIn\s*$/i;
/** A trailing status tag the team types after it: "[SIGNAL 01] Bid-desk hiring · LinkedIn · PAUSED". */
const TRAILING_PAUSED = /\s*[·|-]\s*paused\s*$/i;

/** Whether the name ends in " · LinkedIn", with or without a trailing " · PAUSED" after it. */
export function hasLinkedInSuffix(name: string): boolean {
  return LINKEDIN_SUFFIX.test(name.replace(TRAILING_PAUSED, ''));
}

/** The name without its " · LinkedIn" suffix, for tables where every row carries it (said once in the header). */
export function withoutLinkedInSuffix(name: string): string {
  return name.replace(/\s*·\s*LinkedIn(?=(\s*[·|-]\s*paused)?\s*$)/i, '').trim() || name;
}

/** Statuses that are the normal state of a live campaign: no pill, it would be on every row. */
const QUIET_STATUSES = new Set(['running', 'active', 'started']);

/**
 * A campaign's lemlist status. Running is the default and says nothing; paused,
 * ended or draft get a neutral pill, and errors a red one.
 */
export function CampaignStatus({ campaign, showRunning = false }: { campaign: CampaignRef; showRunning?: boolean }) {
  const status = campaign.status?.toLowerCase() ?? null;
  if (!status) return <Badge>Unknown</Badge>;
  if (QUIET_STATUSES.has(status) && !showRunning) return null;
  const label = status.charAt(0).toUpperCase() + status.slice(1);
  const variant = status === 'error' || status === 'failed' ? 'error' : QUIET_STATUSES.has(status) ? 'success' : 'default';
  return <Badge variant={variant}>{label}</Badge>;
}

export const STEP_TYPE_LABELS: Record<string, string> = {
  linkedinVisit: 'LI visit',
  linkedinInvite: 'LI invite',
  linkedinSend: 'LI message',
  linkedinVoiceNote: 'LI voice note',
  email: 'Email',
  conditional: 'Condition',
  manual: 'Manual task',
  phone: 'Call',
  api: 'API',
};

export function stepTypeLabel(type: string): string {
  return STEP_TYPE_LABELS[type] ?? type;
}

/** Tooltip shared by every Connected column and tile. */
export const CONNECTED_TOOLTIP =
  'Of the people sent a LinkedIn invite in the period, how many have accepted so far: the same cohort as Outbound activity. Its base is people invited, which can be a little below Contacted: Contacted also counts people reached only by email or a message step.';

/** No lemlist activity of any kind in the selected period. */
export function hasActivity(f: FunnelCounts): boolean {
  return f.touches > 0 || f.contacted > 0 || f.replied > 0 || f.failed > 0 || f.messagesBeforeAccept > 0 || f.lastActivityAt !== null;
}

/**
 * A signal play that has never put anyone into lemlist: nobody enrolled, no
 * leads in its campaign, no activity in the period. These collapse into one
 * line on the overview and get no tab of their own.
 */
export function isDormantSignal(s: { candidates: { enrolled: number }; leadsInCampaign: number; funnel: FunnelCounts }): boolean {
  return s.candidates.enrolled === 0 && s.leadsInCampaign === 0 && !hasActivity(s.funnel);
}

/** Summed funnel for a set of campaigns. Distinct-lead counts add up because lemlist lead ids are per campaign. */
export function sumFunnels(funnels: FunnelCounts[]): FunnelCounts {
  const total = funnels.reduce(
    (acc, f) => ({
      contacted: acc.contacted + f.contacted,
      invited: acc.invited + f.invited,
      connected: acc.connected + f.connected,
      replied: acc.replied + f.replied,
      interested: acc.interested + f.interested,
      notInterested: acc.notInterested + f.notInterested,
      failed: acc.failed + f.failed,
      messagesBeforeAccept: acc.messagesBeforeAccept + f.messagesBeforeAccept,
      touches: acc.touches + f.touches,
      lastActivityAt:
        f.lastActivityAt && (!acc.lastActivityAt || f.lastActivityAt > acc.lastActivityAt)
          ? f.lastActivityAt
          : acc.lastActivityAt,
    }),
    {
      contacted: 0,
      invited: 0,
      connected: 0,
      replied: 0,
      interested: 0,
      notInterested: 0,
      failed: 0,
      messagesBeforeAccept: 0,
      touches: 0,
      lastActivityAt: null as string | null,
    },
  );
  return {
    ...total,
    connectRate: ratio(total.connected, total.invited),
    replyRate: ratio(total.replied, total.contacted),
    interestedRate: ratio(total.interested, total.contacted),
  };
}

/**
 * "Last activity" is the newest lemlist event of any kind on the row, not only
 * a touch: a reply, an accepted invite or a profile visit counts. So it can
 * fall in a week where the rep made 0 touches (Patrick and Andrew, Oct 2026).
 */
export const LAST_ACTIVITY_TOOLTIP =
  'The newest lemlist event of any kind: a touch, but also a reply, an accepted invite or a profile visit. It can fall in a week where the rep made 0 touches.';
